/**
 * Microphone adapter.
 *
 * The mic is an external boundary (per AGENTS.md), so MicPanel and the
 * audio predict panel depend on this interface — never on
 * `navigator.mediaDevices` directly. Tests pass a stub adapter; production
 * code uses `browserMicrophoneAdapter` which talks to real WebRTC + Web
 * Audio API.
 *
 * Capture format: a 1-second mono spectrogram, computed live from an
 * AnalyserNode. The same FFT params are used at training and prediction
 * time so the kid's spectrograms are dimensionally identical to the ones
 * the trained CNN sees during inference.
 */

export type MicrophoneErrorKind =
  | 'permission-denied'
  | 'no-microphone'
  | 'unsupported'
  | 'in-use'
  | 'unknown';

export interface MicrophoneError {
  kind: MicrophoneErrorKind;
  message: string;
}

/** Threshold below which a recording is considered silent. Real-mic input
 *  always sees some background noise (PCM peaks ~0.001 even in a quiet
 *  room); a peak below this means the AudioContext was suspended or the
 *  mic was muted at the OS level — either way, the kid's recording would
 *  be useless training data. */
export const SILENCE_PEAK_THRESHOLD = 0.0005;

/** Custom error thrown when a recording captured no audible audio. The
 *  UI surfaces this distinctly so the kid sees "your mic isn't picking
 *  up sound" instead of silently poisoning their training set. */
export class SilentRecordingError extends Error {
  constructor() {
    super('No sound was picked up. Check that your microphone is on and not muted.');
    this.name = 'SilentRecordingError';
  }
}

export function isSilentRecordingError(err: unknown): err is SilentRecordingError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name: unknown }).name === 'SilentRecordingError'
  );
}

/**
 * Spectrogram capture parameters. These values are chosen to match the
 * shape Teachable Machine's audio classifier uses — 43 frames × 232 bins
 * over a 1-second window — so the resulting CNN size + per-frame timing
 * are sane Chromebook-class targets.
 */
export const FFT_SIZE = 1024;
/** Frequency bins kept after the AnalyserNode's getFloatFrequencyData. */
export const SPECTROGRAM_BINS = 232;
/** Number of time-axis frames in a 1-second clip. */
export const SPECTROGRAM_FRAMES = 43;
/** Milliseconds between successive frames. ~23ms × 43 ≈ 1 second. */
export const FRAME_INTERVAL_MS = 23;

export interface MicrophoneStream {
  /** Live MediaStream — kept around so we can stop tracks on close. */
  stream: MediaStream;
  /** AudioContext that owns the analyser. Closed on `close()`. */
  context: AudioContext;
  /** The single MediaStreamAudioSourceNode for this stream. Anything that
   *  needs raw audio (the recorder, the analyser feeding the live
   *  spectrogram) connects to the same node — creating a second
   *  MediaStreamSource against the same MediaStream silently disconnects
   *  the first in some browsers, which was the cause of "the recording
   *  panel shows nothing AND every recording was silence" in production. */
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
}

export interface RecordedAudio {
  /** Mono PCM, range -1..1, at `sampleRate`. */
  pcm: Float32Array;
  sampleRate: number;
}

export interface MicrophoneAdapter {
  /** Opens a mic stream wired to an AnalyserNode at the studio's FFT params. */
  open(): Promise<MicrophoneStream>;
  /** Stops all tracks and closes the AudioContext. Idempotent. */
  close(handle: MicrophoneStream): void;
  /**
   * Captures a single 1-second spectrogram from the open mic. Returns the
   * raw Float32 spectrogram values (length SPECTROGRAM_FRAMES * SPECTROGRAM_BINS).
   * Used by the live-prediction loop, where rolling AnalyserNode reads
   * are cheaper than re-decoding stored audio.
   */
  captureSpectrogram(handle: MicrophoneStream): Promise<Float32Array>;
  /**
   * Records `seconds` of mono audio from the open mic and returns it as
   * raw PCM. Used by the MicPanel's record-N-then-extract flow so the
   * kid can listen back before deciding whether to extract samples from
   * the recording. Throws SilentRecordingError if no audible signal is
   * detected — better to fail loudly than to ship silence as training
   * data.
   */
  recordSeconds(handle: MicrophoneStream, seconds: number): Promise<RecordedAudio>;
  /**
   * Captures a fresh `seconds`-long PCM window for live prediction. Unlike
   * `recordSeconds` this does NOT enforce a silence check — at predict
   * time silence is a legitimate input (the model can correctly answer
   * "I'm not sure" or "Background noise"). The audio predict panel uses
   * this so its inference path runs on the SAME spectrogram pipeline as
   * training: live PCM → pcmToSpectrogram → predict, instead of the old
   * AnalyserNode dBFS path which had a different value distribution and
   * caused the CNN to always pick the majority class.
   */
  captureWindow(handle: MicrophoneStream, seconds: number): Promise<RecordedAudio>;
}

export const browserMicrophoneAdapter: MicrophoneAdapter = {
  async open() {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw micError('unsupported', 'This browser does not support microphone access.');
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
        video: false,
      });
    } catch (err) {
      throw fromGetUserMediaError(err);
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      stream.getTracks().forEach((t) => t.stop());
      throw micError('unsupported', 'This browser does not support the Web Audio API.');
    }
    const context = new Ctor();
    // Best-effort resume — most callers have just come from a click, but
    // a re-entrant open (e.g. retry from the error overlay) can't always
    // count on user activation still being live. recordSeconds and
    // captureSpectrogram both also call resume() defensively.
    if (context.state === 'suspended') {
      try {
        await context.resume();
      } catch {
        // Ignore — the per-operation resume below will retry.
      }
    }
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    analyser.smoothingTimeConstant = 0;
    source.connect(analyser);
    return { stream, context, source, analyser };
  },

  close(handle) {
    try {
      for (const track of handle.stream.getTracks()) track.stop();
    } catch {
      // Already stopped — fine.
    }
    if (handle.context.state !== 'closed') {
      void handle.context.close().catch(() => {
        // Already closing or closed; ignore.
      });
    }
  },

  async captureSpectrogram(handle) {
    await ensureRunning(handle.context);
    return captureSpectrogramFromAnalyser(handle.analyser);
  },

  async recordSeconds(handle, seconds) {
    await ensureRunning(handle.context);
    return recordPcmFromContext(handle, seconds, { enforceSignal: true });
  },

  async captureWindow(handle, seconds) {
    await ensureRunning(handle.context);
    return recordPcmFromContext(handle, seconds, { enforceSignal: false });
  },
};

async function ensureRunning(context: AudioContext): Promise<void> {
  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch {
      // best-effort
    }
  }
}

/**
 * Pull `seconds` worth of mono PCM off the open mic by attaching a
 * ScriptProcessor (deprecated but universally supported and good enough
 * for short captures) to the analyser's source. Resolves once the buffer
 * is filled; falls back to whatever the AudioContext gave us at its
 * native sample rate so the caller can resample to a uniform target.
 *
 * Reuses `handle.source` rather than creating a new MediaStreamSource —
 * a second source on the same MediaStream silently disconnected the
 * analyser in production Chrome, which made every recording silent and
 * the live spectrogram blank. Throws SilentRecordingError if the captured
 * peak amplitude is below the noise floor — better to fail loudly than
 * to feed silent clips into training.
 */
async function recordPcmFromContext(
  handle: MicrophoneStream,
  seconds: number,
  opts: { enforceSignal: boolean } = { enforceSignal: true },
): Promise<RecordedAudio> {
  const { context, source } = handle;
  const targetSamples = Math.floor(seconds * context.sampleRate);
  const recorded = new Float32Array(targetSamples);
  let written = 0;
  // 4096-sample buffer @ 48kHz ≈ 85ms — plenty fine-grained for a
  // 2–20 second capture and avoids dropping samples on a busy thread.
  const node = context.createScriptProcessor(4096, 1, 1);
  // The ScriptProcessor must be connected to the destination for
  // onaudioprocess to fire in some browsers; route through a muted
  // gain so we don't loop captured audio back to the kid.
  const muted = context.createGain();
  muted.gain.value = 0;
  return new Promise<RecordedAudio>((resolve, reject) => {
    let settled = false;
    const cleanup = (): void => {
      try {
        node.disconnect();
      } catch {
        // already disconnected
      }
      try {
        source.disconnect(node);
      } catch {
        // already disconnected
      }
      try {
        muted.disconnect();
      } catch {
        // already disconnected
      }
    };
    const finish = (): void => {
      if (settled) return;
      settled = true;
      cleanup();
      const pcm = recorded.subarray(0, written);
      // Real microphone audio always has *some* signal — even a quiet
      // room reads ~0.001 RMS. A peak below the silence threshold means
      // the AudioContext was suspended or the OS muted the mic; either
      // way the clip is useless training data.
      if (opts.enforceSignal) {
        let peak = 0;
        for (let i = 0; i < pcm.length; i++) {
          const a = Math.abs(pcm[i] ?? 0);
          if (a > peak) peak = a;
        }
        if (peak < SILENCE_PEAK_THRESHOLD) {
          reject(new SilentRecordingError());
          return;
        }
      }
      resolve({ pcm, sampleRate: context.sampleRate });
    };
    const fail = (err: unknown): void => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error('Could not record from the microphone.'));
    };
    node.onaudioprocess = (event): void => {
      try {
        const channel = event.inputBuffer.getChannelData(0);
        const remaining = targetSamples - written;
        const take = Math.min(channel.length, remaining);
        recorded.set(channel.subarray(0, take), written);
        written += take;
        if (written >= targetSamples) finish();
      } catch (err) {
        fail(err);
      }
    };
    source.connect(node);
    node.connect(muted);
    muted.connect(context.destination);
  });
}

/**
 * Reusable: read SPECTROGRAM_FRAMES × SPECTROGRAM_BINS off an AnalyserNode
 * over ~1 second. Exported so the live-prediction loop can pull
 * sliding-window frames using the same shape the model was trained on.
 */
export async function captureSpectrogramFromAnalyser(
  analyser: AnalyserNode,
): Promise<Float32Array> {
  const out = new Float32Array(SPECTROGRAM_FRAMES * SPECTROGRAM_BINS);
  const frame = new Float32Array(analyser.frequencyBinCount);
  for (let i = 0; i < SPECTROGRAM_FRAMES; i++) {
    analyser.getFloatFrequencyData(frame);
    for (let b = 0; b < SPECTROGRAM_BINS; b++) {
      // Replace -Infinity (silent bin) with the analyser's noise floor so
      // the CNN never sees NaN or non-finite gradients.
      const v = frame[b] ?? -100;
      out[i * SPECTROGRAM_BINS + b] = Number.isFinite(v) ? v : -100;
    }
    if (i < SPECTROGRAM_FRAMES - 1) {
      await new Promise<void>((r) => setTimeout(r, FRAME_INTERVAL_MS));
    }
  }
  return out;
}

export function isMicrophoneError(err: unknown): err is MicrophoneError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'kind' in err &&
    'message' in err &&
    typeof (err as { kind: unknown }).kind === 'string'
  );
}

function micError(kind: MicrophoneErrorKind, message: string): MicrophoneError {
  return { kind, message };
}

function fromGetUserMediaError(err: unknown): MicrophoneError {
  if (err && typeof err === 'object' && 'name' in err) {
    const name = (err as { name: string }).name;
    switch (name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return micError(
          'permission-denied',
          'The microphone is blocked. Allow mic access in your browser to use this.',
        );
      case 'NotFoundError':
      case 'OverconstrainedError':
        return micError('no-microphone', 'No microphone was found on this device.');
      case 'NotReadableError':
      case 'AbortError':
        return micError('in-use', 'The microphone is busy. Close other apps using it and retry.');
      default:
        return micError('unknown', 'Could not turn on the microphone.');
    }
  }
  return micError('unknown', 'Could not turn on the microphone.');
}
